import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  KeyRound,
  Monitor,
  Shield as ShieldIcon,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import {
  changePassword,
  disableMfa,
  getMySessions,
  revokeSession,
  updateMe,
} from '@/services/api.client';
import { formatRelative } from '@/lib/utils';

// ─── Profile form ─────────────────────────────────────────────────────────────

const profileSchema = z.object({
  fullName: z.string().min(2, 'Enter your full name'),
  phone: z.string().optional(),
});
type ProfileValues = z.infer<typeof profileSchema>;

function ProfileSection() {
  const { user, refetch } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { fullName: user?.fullName ?? '', phone: user?.phone ?? '' },
  });

  useEffect(() => {
    if (user) reset({ fullName: user.fullName, phone: user.phone ?? '' });
  }, [user, reset]);

  const mutation = useMutation({
    mutationFn: (v: ProfileValues) =>
      updateMe({ fullName: v.fullName, ...(v.phone ? { phone: v.phone } : {}) }),
    onSuccess: () => refetch(),
  });

  const apiError =
    mutation.isError && mutation.error instanceof Object && 'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
        <CardDescription>Update your name and phone number.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)} noValidate>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="fullName">Full name</FieldLabel>
              <Input id="fullName" aria-invalid={!!errors.fullName} {...register('fullName')} />
              {errors.fullName && (
                <p className="text-sm text-destructive">{errors.fullName.message}</p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input id="email" value={user?.email ?? ''} disabled className="opacity-60" />
            </Field>
            <Field>
              <FieldLabel htmlFor="phone">Phone</FieldLabel>
              <Input id="phone" type="tel" placeholder="+254 700 000 000" {...register('phone')} />
            </Field>
            {apiError && <p className="text-sm text-destructive">{apiError}</p>}
            {mutation.isSuccess && (
              <p className="text-sm text-[var(--status-green-text)]">Profile updated.</p>
            )}
            <Field>
              <Button type="submit" disabled={mutation.isPending || !isDirty}>
                {mutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── Two-factor authentication ────────────────────────────────────────────────

function TwoFactorSection() {
  const { user, refetch } = useAuth();
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const enabled = user?.mfaEnabled ?? false;

  const disableMutation = useMutation({
    mutationFn: () => disableMfa(disablePassword),
    onSuccess: () => {
      toast.success('Two-factor authentication turned off');
      refetch();
      setDisableOpen(false);
      setDisablePassword('');
    },
    onError: () => toast.error('Could not turn off two-factor. Check your password and try again.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Two-factor authentication
          <Badge
            variant="outline"
            className={
              enabled
                ? 'text-[10px] py-0 px-1.5 h-4 border-[var(--status-green)] text-[var(--status-green-text)]'
                : 'text-[10px] py-0 px-1.5 h-4'
            }
          >
            {enabled ? 'On' : 'Off'}
          </Badge>
        </CardTitle>
        <CardDescription>
          Adds an extra step at sign-in using a code from your authenticator app.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {enabled ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-[var(--text-secondary)]">
              Two-factor is keeping your account safer.
            </p>
            <Button variant="outline" size="sm" onClick={() => setDisableOpen(true)}>
              Turn off
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-[var(--text-secondary)]">
              We'll guide you through scanning a QR with your authenticator app.
            </p>
            <Button asChild size="sm">
              <Link to="/mfa-setup">
                <KeyRound className="size-4 mr-1.5" />
                Turn on
              </Link>
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn off two-factor authentication?</DialogTitle>
            <DialogDescription>
              Your account will be less secure. You can turn it back on at any time.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="disable-mfa-password">Confirm your password</FieldLabel>
            <Input
              id="disable-mfa-password"
              type="password"
              autoComplete="current-password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDisableOpen(false)}
              disabled={disableMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => disableMutation.mutate()}
              disabled={disableMutation.isPending || disablePassword.length === 0}
            >
              {disableMutation.isPending ? 'Turning off…' : 'Turn off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Change password ──────────────────────────────────────────────────────────

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z
      .string()
      .min(8, 'At least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
        'Must include uppercase, lowercase, and a digit',
      ),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type PasswordValues = z.infer<typeof passwordSchema>;

function PasswordSection() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema) });

  const mutation = useMutation({
    mutationFn: (v: PasswordValues) => changePassword(v.currentPassword, v.newPassword),
    onSuccess: () => reset(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Change Password</CardTitle>
        <CardDescription>Use a strong password with 8+ characters.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)} noValidate>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="currentPassword">Current password</FieldLabel>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                aria-invalid={!!errors.currentPassword}
                {...register('currentPassword')}
              />
              {errors.currentPassword && (
                <p className="text-sm text-destructive">{errors.currentPassword.message}</p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="newPassword">New password</FieldLabel>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!errors.newPassword}
                {...register('newPassword')}
              />
              {errors.newPassword && (
                <p className="text-sm text-destructive">{errors.newPassword.message}</p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="confirmPassword">Confirm new password</FieldLabel>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!errors.confirmPassword}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </Field>
            {mutation.isError && (
              <p className="text-sm text-destructive">Incorrect current password.</p>
            )}
            {mutation.isSuccess && (
              <p className="text-sm text-[var(--status-green-text)]">Password updated.</p>
            )}
            <Field>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Updating…' : 'Update password'}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── Active sessions ──────────────────────────────────────────────────────────

function SessionsSection() {
  const qc = useQueryClient();
  const { data: sessions, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: getMySessions,
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeSession(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sessions'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Signed-in devices</CardTitle>
        <CardDescription>
          Devices currently signed in to your account. Sign out any device you don't recognise.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-3 items-center">
                <Skeleton className="size-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            ))}
          </div>
        ) : sessions?.length ? (
          <div className="divide-y divide-border/50">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center gap-3 py-3">
                <div className="size-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                  {s.platform?.toLowerCase().includes('mobile') ? (
                    <Smartphone className="size-4 text-muted-foreground" />
                  ) : (
                    <Monitor className="size-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">
                      {s.deviceName ?? s.platform ?? 'Unknown device'}
                    </p>
                    {s.isCurrent && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        This device
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Last active {formatRelative(s.lastActiveAt)}
                    {s.location ? ` · ${s.location}` : ''}
                  </p>
                </div>
                {!s.isCurrent && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => revokeMut.mutate(s.id)}
                    disabled={revokeMut.isPending}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No other devices signed in right now.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Preferences index ────────────────────────────────────────────────────────

const PREFERENCE_LINKS = [
  {
    to: '/manager/account/privacy',
    icon: ShieldIcon,
    title: 'Your data',
    description: 'Download a copy, correct what we have, or delete your account',
  },
] as const;

function PreferencesSection() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Preferences</CardTitle>
        <CardDescription>Privacy settings.</CardDescription>
      </CardHeader>
      <CardContent className="px-2 pb-2">
        <div className="divide-y divide-border/50">
          {PREFERENCE_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-3 py-3 px-3 rounded-md hover:bg-[var(--bg-surface-raised)] transition-colors"
            >
              <div className="size-9 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
                <link.icon className="size-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)]">{link.title}</p>
                <p className="text-xs text-[var(--text-tertiary)]">{link.description}</p>
              </div>
              <ChevronRight className="size-4 text-[var(--text-tertiary)] shrink-0" />
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ManagerAccountPage() {
  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl">Account</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your profile, preferences, and security
        </p>
      </div>

      <ProfileSection />
      <PreferencesSection />
      <PasswordSection />
      <TwoFactorSection />
      <SessionsSection />
    </div>
  );
}
